<template>
  <div>
    <label
      v-if="label"
      for="price"
      class="block text-sm font-medium leading-5 text-true-gray-700"
      >{{ label }}</label
    >
    <div class="mt-1 relative group">
      <div
        v-if="hasIcon"
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
      >
        <!-- Heroicon name: mail -->
        <svg
          class="w-4 h-4 text-true-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>
      <input
        ref="inputRef"
        class="rounded border focus:border-2 outline-none focus:border-yellow-500 block w-full pr-12 sm:text-sm sm:leading-5 text-true-gray-700 placeholder-gray-200"
        :class="[
          hasIcon ? 'pl-10' : 'pl-3',
          {
            sm: 'py-1',
            default: 'py-3',
          }[size],
        ]"
        :placeholder="placeholder"
        aria-describedby="price-currency"
        :value="value"
        @input="setValue($event.target.value)"
        @focus="emit('focus')"
        @blur="emit('blur', $event)"
        @keyup="emit('keyup', $event)"
        :disabled="disabled"
      />
      <div
        v-if="clearable"
        class="absolute inset-y-0 right-0 pr-3 flex items-center"
      >
        <svg
          v-if="!!value"
          class="w-5 h-5 text-true-gray-400 hover:text-gray-500 opacity-0 group-hover:opacity-100 hover:cursor-pointer"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          @click="clearInput()"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div
          v-if="options.length > 0"
          class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
        >
          <div class="py-1">
            <div v-for="{ value, label } in optionsInner" :key="value">
              <a
                href="#"
                :class="[
                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                  'block px-4 py-2 text-sm',
                ]"
                >{{ label }}</a
              >
            </div>
          </div>
        </div>
      </transition>
      <p
        v-for="(error, idx) in errors"
        :key="idx"
        class="text-red-400 text-xs my-1"
      >
        {{ error }}
      </p>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from '@vue/composition-api'
import { isString } from 'lodash'

export default {
  props: {
    label: {
      type: String,
    },
    value: {
      type: [String, Number],
    },
    placeholder: {
      type: String,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    hasIcon: {
      type: Boolean,
      default: false,
    },
    focusOnMount: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'default',
      // possible values: 'sm', 'default'
    },
    options: {
      type: Array,
      default: () => [],
    },
    errors: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const setValue = (value) => {
      emit('input', value)
    }

    const clearInput = () => {
      setValue('')
    }

    const inputRef = ref(false)

    const optionsInner = computed(() => {
      if (props.options.length > 0 && isString(props.options[0])) {
        return props.options.map((value) => ({
          value,
          label: value,
        }))
      } else {
        return props.options
      }
    })

    onMounted(() => {
      if (props.focusOnMount) {
        inputRef.value.focus()
      }
    })

    return {
      inputRef,
      emit,
      setValue,
      clearInput,
      optionsInner,
    }
  },
}
</script>
